import { useEffect, useMemo, useRef } from "react";

import { useTranslation } from "react-i18next";

import { fromWindows, useAvailability } from "../useAvailability";
import type { AvailabilityWindow } from "../schemas/availability";
import type { DayTimes } from "../types";
import {
  formatMinute,
  getMonthsShort,
  getWeekdaysShort,
} from "../constants";
import { rangeValid } from "../validation";
import { MonthCalendar } from "./MonthCalendar";
import { TimeRanges } from "./TimeRanges";

export interface AvailabilityPickerProps {
  minDate?: Date;
  initialWindows?: Array<AvailabilityWindow>;
  onChange?: (windows: Array<AvailabilityWindow>) => void;
  onContinue?: (windows: Array<AvailabilityWindow>) => void;
  showContinue?: boolean;
  continueLabel?: string;
}

export function AvailabilityPicker({
  minDate,
  initialWindows,
  onChange,
  onContinue,
  showContinue = true,
  continueLabel,
}: AvailabilityPickerProps) {
  const { t, i18n } = useTranslation("common");
  const lng = i18n.language || "en";

  // initialWindows is read once on mount; later changes don't re-seed state.
  const initialStateRef = useRef<ReturnType<typeof fromWindows> | undefined>(
    initialWindows && initialWindows.length
      ? fromWindows(initialWindows)
      : undefined,
  );
  const c = useAvailability(initialStateRef.current);
  const { state, isValid } = c;
  const perDay = state.mode === "PER_DAY";
  const dates = [...state.dates].sort();

  const weekdaysShort = useMemo(() => getWeekdaysShort(lng), [lng]);
  const monthsShort = useMemo(() => getMonthsShort(lng), [lng]);
  const midnightLabel = t("availabilityPicker.midnight");

  function dateLabel(iso: string): string {
    const [y, m, d] = iso.split("-").map(Number);
    const wd = new Date(y, m - 1, d).getDay();
    return `${weekdaysShort[wd]} ${monthsShort[m - 1]} ${d}`;
  }

  function timeLabel(dt: DayTimes): string {
    if (dt.anyTime) return t("availabilityPicker.anyTime");
    const valid = dt.ranges.filter(rangeValid);
    if (valid.length === 0) return "—";
    return valid
      .map(
        (r) =>
          `${formatMinute(r.startMinute, lng, midnightLabel)}–${formatMinute(r.endMinute, lng, midnightLabel)}`,
      )
      .join("; ");
  }

  // Callers commonly pass an inline arrow for onChange; stash it in a ref so
  // identity churn doesn't trigger this effect on every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current?.(c.toWindows());
    // c.toWindows is stable when state is stable; depend on state only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  let summary: string;
  if (dates.length === 0) {
    summary = t("availabilityPicker.summaryEmpty");
  } else if (!perDay) {
    summary = t("availabilityPicker.summaryShared", {
      count: dates.length,
      dates: dates.map(dateLabel).join(", "),
      time: timeLabel(state.shared),
    });
  } else {
    summary = dates
      .map(
        (d) =>
          `${dateLabel(d)}: ${timeLabel(state.perDay[d] ?? { anyTime: false, ranges: [] })}`,
      )
      .join(" · ");
  }

  const resolvedContinueLabel =
    continueLabel ?? t("availabilityPicker.continue");

  return (
    <div className="av-root">
      <div className="av-panel">
        <MonthCalendar
          selectedDates={state.dates}
          minDate={minDate}
          onToggle={c.toggleDate}
        />

        <div className="av-toggle-row">
          <span className="av-toggle-label">
            {t("availabilityPicker.differentTimePerDay")}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={perDay}
            aria-label={t("availabilityPicker.differentTimePerDayAria")}
            className={`av-switch${perDay ? " is-on" : ""}`}
            onClick={() => c.setMode(perDay ? "SHARED" : "PER_DAY")}
          />
        </div>

        {!perDay ? (
          <div className="av-card">
            <div className="av-applies">
              {t("availabilityPicker.appliesAllDays")}
            </div>
            <TimeRanges
              value={state.shared}
              target="shared"
              controller={c}
              idPrefix="shared"
            />
          </div>
        ) : dates.length === 0 ? (
          <p className="av-empty">
            {t("availabilityPicker.selectDaysFirst")}
          </p>
        ) : (
          dates.map((d) => {
            const dt = state.perDay[d] ?? { anyTime: false, ranges: [] };
            return (
              <div className="av-card" key={d}>
                <div className="av-card-head">
                  <span className="av-card-title">{dateLabel(d)}</span>
                </div>
                <TimeRanges
                  value={dt}
                  target={d}
                  controller={c}
                  idPrefix={d}
                />
              </div>
            );
          })
        )}

        <div className="av-footer">
          <p className="av-summary">{summary}</p>
          <div className="av-actions">
            <button
              type="button"
              className="av-reset"
              disabled={dates.length === 0}
              onClick={c.reset}
            >
              {t("availabilityPicker.resetChoices")}
            </button>
            {showContinue && (
              <button
                type="button"
                className="av-continue"
                disabled={!isValid}
                onClick={() => onContinue?.(c.toWindows())}
              >
                {resolvedContinueLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvailabilityPicker;
